import {Component, OnDestroy, OnInit} from "@angular/core";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {obtenerNombresTipoAccion, TipoAccionesEnum} from "@shared/enums";
import {Entrada} from "@shared/interfaces";
import {Nilable} from "@shared/utils";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {concat, firstValueFrom, mergeMap, Observable, of, Subject, Subscription} from "rxjs";
import {AccionesItemDTO, GrupoDTO, InvestigacionDTO, TagDTO} from "@DTOS";
import {debounceTime, distinctUntilChanged, filter, tap} from "rxjs/operators";
import {EtiquetasAcciones, InvestigacionesAcciones, State} from "@shared/states";
import {Store} from "@ngxs/store";
import {InvestigacionesService} from "@shared/servicesBackend";
import {EncuestasService} from "../../../layout/encuestas/encuestas.service";
import {
    TiposPreguntasService
} from "../../../layout/encuestas/encuestas-compartidos/encuesta-detalle-pregunta/tipos-preguntas.service";
import {ItemPreguntaModelDTO} from "@Models";

@Component({
    selector: "app-modal-acciones-item",
    templateUrl: "./modal-acciones-item.component.html",
    styleUrls: ["./modal-acciones-item.component.scss"]
})
export class ModalAccionesItemComponent implements OnInit, OnDestroy {
    public tiposAcciones: Entrada<TipoAccionesEnum>[] = obtenerNombresTipoAccion().map(([tipo, nombre]) => ({
        clave: tipo,
        valor: nombre
    }));
    public accionTipoControl = new FormControl(TipoAccionesEnum.ADD_TAG_TO_USER, { nonNullable: true });
    public formGroupAccion: Nilable<FormGroup>;
    public acciones: AccionesItemDTO[] = [];

    public investigacionesBuscadas$: Observable<InvestigacionDTO[]>;
    public investigacionBuscarInput$ = new Subject<string>();
    public etiquetas: TagDTO[];
    public grupos: GrupoDTO[];
    public agregarColapsado = true;

    public guardarAccionItem$: Observable<AccionesItemDTO>;
    public borrarAccionItem$: Observable<AccionesItemDTO>;

    public TipoAccionesEnum = TipoAccionesEnum;

    private subs = new Subscription();
    private subsFormGroup = new Subscription();
    private _guardarAccionItem$ = new Subject<AccionesItemDTO>();
    private _borrarAccionItem$ = new Subject<AccionesItemDTO>();

    constructor(private formBuilder: FormBuilder, private activeModal: NgbActiveModal, private store: Store,
        private investigacionesService: InvestigacionesService, private encuestasService: EncuestasService,
        private tiposPreguntasService: TiposPreguntasService) {
        this.guardarAccionItem$ = this._guardarAccionItem$.asObservable();
        this.borrarAccionItem$ = this._borrarAccionItem$.asObservable();
    }

    ngOnInit(): void {
        this.subs.add(
            this.accionTipoControl.valueChanges.pipe(
                mergeMap(tipo => this.generarFormGroup(tipo))
            ).subscribe()
        );
        void this.generarFormGroup(this.accionTipoControl.value);
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
        this.subsFormGroup.unsubscribe();
    }

    public trackById(_:  number, item: AccionesItemDTO): number {
        return item.id;
    }

    public async cargarItem(item: ItemPreguntaModelDTO) {
        const accionesEncuesta = await firstValueFrom(
            this.encuestasService.obtenerAccionesItem(this.tiposPreguntasService.encuesta)
        );
        this.acciones = accionesEncuesta.filter(accionEncuesta => accionEncuesta.item === item.Id);
    }

    public abrirFormulario() {
        this.agregarColapsado = false;
    }

    public cerrarFormulario() {
        this.agregarColapsado = true;
    }

    public cancelar() {
        this.activeModal.dismiss();
    }

    public guardar() {
        const tipo = this.accionTipoControl.value as TipoAccionesEnum;
        const accion = new AccionesItemDTO();
        switch (tipo) {
        case TipoAccionesEnum.ADD_USER_TO_CAMPAIGN:
        case TipoAccionesEnum.REMOVE_USER_FROM_CAMPAIGN:
            accion.type = tipo;
            accion.payload.grupoId = this.formGroupAccion?.value.grupo.id;
            break;
        case TipoAccionesEnum.ADD_TAG_TO_USER:
        case TipoAccionesEnum.REMOVE_TAG_FROM_USER:
            accion.type = tipo;
            accion.payload.tagId = this.formGroupAccion?.value.etiqueta.id;
        }
        this._guardarAccionItem$.next(accion);
        this.cerrarFormulario();
    }

    public borrar(accion: AccionesItemDTO) {
        this._borrarAccionItem$.next(accion);
    }

    private async generarFormGroup(tipo: TipoAccionesEnum) {
        this.limpiarSubsFormGroup();
        if ([TipoAccionesEnum.ADD_USER_TO_CAMPAIGN, TipoAccionesEnum.REMOVE_USER_FROM_CAMPAIGN].includes(tipo)) {
            await this.cargarDataUsuarioGrupoAsync();
            this.generarFormGroupUsuarioGrupo();
        }
        if ([TipoAccionesEnum.ADD_TAG_TO_USER, TipoAccionesEnum.REMOVE_TAG_FROM_USER].includes(tipo)) {
            await this.cargarDataEtiquetaUsuarioAsync();
            this.generarFormGroupEtiquetaUsuario();
        }
    }

    private limpiarSubsFormGroup() {
        this.subsFormGroup.unsubscribe();
        this.subsFormGroup = new Subscription();
    }

    private generarFormGroupUsuarioGrupo() {
        this.formGroupAccion = this.formBuilder.group({
            investigacion: [null, Validators.required],
            grupo: [null, Validators.required]
        });
        this.formGroupAccion.controls.grupo.disable();
        this.subsFormGroup.add(
            this.formGroupAccion.valueChanges.pipe(
                mergeMap(formGroup => this.investigacionesService.obtenerGrupos(formGroup.investigacion))
            ).subscribe(grupos => {
                this.grupos = grupos;
                this.formGroupAccion!.controls.grupo.enable({ emitEvent: false });
            })
        );
    }

    private generarFormGroupEtiquetaUsuario() {
        this.formGroupAccion = this.formBuilder.group({
            etiqueta: [null, Validators.required]
        });
    }

    private async cargarDataUsuarioGrupoAsync() {
        this.investigacionesBuscadas$ = concat(
            of([]),
            this.investigacionBuscarInput$.pipe(
                debounceTime(200),
                distinctUntilChanged(),
                tap(term => this.store.dispatch(new InvestigacionesAcciones.Buscar(10, term))),
                mergeMap(term => this.store.select((state: State) => state.investigaciones.investigaciones).pipe(
                    filter(investigaciones => investigaciones.length !== 0 &&
                        investigaciones.some(investigacion => investigacion.nombre.includes(term))
                    )
                ))
            )
        );
    }

    private async cargarDataEtiquetaUsuarioAsync() {
        this.store.dispatch(new EtiquetasAcciones.ObtenerTodo());
        this.etiquetas = await firstValueFrom(
            this.store.select((state: State) => state.etiquetas.etiquetas).pipe(
                filter(etiquetas => etiquetas.length !== 0)
            )
        );
    }
}
